import { useState } from 'react';
import { ArrowsClockwise, Plus, Trash, FloppyDisk } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n/I18nContext';

const STATUS_BADGE = {
  idle: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  cloning: 'bg-amber-50 text-amber-800 border-amber-200',
  ready: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

// Mirror of backend ``_validate_gitlab_url`` in projects_dto.py.
// Returns null if valid, otherwise a translation-key suffix to show the user.
function gitlabUrlError(raw) {
  const v = (raw || '').trim();
  if (!v) return 'empty';
  // SSH-style: ``git@host:group/project[.git]`` — must have a colon after the
  // ``git@`` prefix. NOTE: JS's ``.split(sep, limit)`` caps the *output array
  // length*, not the number of splits — so use ``.slice`` instead.
  const sshOk = v.startsWith('git@') && v.slice('git@'.length).includes(':');
  const httpsOk = v.startsWith('http://') || v.startsWith('https://');
  if (!sshOk && !httpsOk) return 'format';
  if (httpsOk) {
    try {
      const u = new URL(v);
      if (!u.host || !u.pathname.replace(/^\/+|\/+$/g, '')) return 'incomplete';
    } catch {
      return 'format';
    }
  }
  return null;
}

function StatusBadge({ status }) {
  const t = useT();
  const cls = STATUS_BADGE[status] || STATUS_BADGE.idle;
  const label = t(`settings.repoStatus.${status || 'idle'}`);
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${cls}`}>
      {label}
    </span>
  );
}

function RepoRow({ project, repo, isOnlyRepo, onProjectChange }) {
  const t = useT();
  const { user } = useAuth();
  const isOwner = user && project.owner_id === user.id;
  const [form, setForm] = useState({
    name: repo.name,
    gitlab_url: repo.gitlab_url,
    gitlab_token: '',
    default_branch: repo.default_branch || 'main',
    branch_prefix: repo.branch_prefix || '',
  });
  const [saving, setSaving] = useState(false);
  const [recloning, setRecloning] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const dirty = (
    form.name !== repo.name
    || form.gitlab_url !== repo.gitlab_url
    || form.default_branch !== (repo.default_branch || 'main')
    || form.branch_prefix !== (repo.branch_prefix || '')
    || form.gitlab_token.trim().length > 0
  );
  const urlErr = gitlabUrlError(form.gitlab_url);
  const urlChanged = form.gitlab_url !== repo.gitlab_url;
  const urlInvalid = urlChanged && !!urlErr;

  const save = async (e) => {
    e.preventDefault();
    if (!isOwner) return;
    const urlChangedNow = form.gitlab_url !== repo.gitlab_url;
    const urlErrNow = gitlabUrlError(form.gitlab_url);
    if (urlChangedNow && urlErrNow) {
      toast.error(t(`settings.gitlabUrlError.${urlErrNow}`));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        default_branch: form.default_branch,
        branch_prefix: form.branch_prefix,
      };
      if (urlChangedNow) payload.gitlab_url = form.gitlab_url.trim();
      if (form.gitlab_token.trim()) payload.gitlab_token = form.gitlab_token.trim();
      const { data } = await api.patch(`/projects/${project.id}/repos/${repo.id}`, payload);
      toast.success(t('settings.repoSavedToast'));
      onProjectChange && onProjectChange(data);
      setForm((f) => ({ ...f, gitlab_token: '' }));
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.repoSaveFailedToast')));
    } finally {
      setSaving(false);
    }
  };

  const reclone = async () => {
    setRecloning(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/repos/${repo.id}/reclone`);
      toast.success(t('settings.recloneStartedToast'));
      onProjectChange && onProjectChange(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.recloneFailedToast')));
    } finally {
      setRecloning(false);
    }
  };

  const performRemove = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete(`/projects/${project.id}/repos/${repo.id}`);
      toast.success(t('settings.repoRemovedToast'));
      onProjectChange && onProjectChange(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.repoRemoveFailedToast')));
      throw err;
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title={t('settings.repoRemoveConfirmTitle')}
        description={t('settings.repoRemoveConfirmDescription', { name: repo.name })}
        confirmLabel={t('settings.repoRemoveConfirmBtn')}
        variant="destructive"
        loading={removing}
        onConfirm={performRemove}
      />
      <form onSubmit={save} className="rounded-sm border border-zinc-200 bg-white p-3 space-y-2" data-testid={`repo-row-${repo.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs font-semibold text-zinc-900" data-testid={`repo-row-name-${repo.id}`}>{repo.name}</span>
          <StatusBadge status={repo.clone_status} />
          {repo.cloned_at && (
            <span className="text-[10px] text-zinc-500 font-mono">
              {t('settings.lastClonedAtShort', { when: new Date(repo.cloned_at).toLocaleString() })}
            </span>
          )}
          {repo.clone_error && (
            <span className="text-[10px] text-red-600 font-mono truncate" title={repo.clone_error}>
              {repo.clone_error}
            </span>
          )}
        </div>
        {!repo.gitlab_url && isOwner && (
          <div
            data-testid={`repo-missing-url-warning-${repo.id}`}
            className="rounded-sm border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
          >
            {t('settings.repoMissingUrlWarning')}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.repoNameLabel')}</Label>
            <Input
              data-testid={`repo-name-input-${repo.id}`}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!isOwner}
              className="rounded-sm mt-1 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.defaultBranchLabel')}</Label>
            <Input
              data-testid={`repo-branch-input-${repo.id}`}
              value={form.default_branch}
              onChange={(e) => setForm({ ...form, default_branch: e.target.value })}
              disabled={!isOwner}
              className="rounded-sm mt-1 text-xs font-mono"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.branchPrefixLabel')}</Label>
            <Input
              data-testid={`repo-branch-prefix-input-${repo.id}`}
              value={form.branch_prefix}
              onChange={(e) => setForm({ ...form, branch_prefix: e.target.value })}
              disabled={!isOwner}
              className="rounded-sm mt-1 text-xs font-mono"
              placeholder={t('settings.branchPrefixPlaceholder')}
            />
            <p className="text-[10px] text-zinc-500 mt-1">{t('settings.branchPrefixHint')}</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.gitlabUrlLabel')}</Label>
            <Input
              data-testid={`repo-url-input-${repo.id}`}
              value={form.gitlab_url}
              onChange={(e) => setForm({ ...form, gitlab_url: e.target.value })}
              disabled={!isOwner}
              aria-invalid={urlInvalid || undefined}
              className={`rounded-sm mt-1 text-xs font-mono ${urlInvalid ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
              placeholder="https://gitlab.com/group/project"
            />
            {urlInvalid ? (
              <p className="text-[10px] text-red-600 mt-1" data-testid={`repo-url-error-${repo.id}`}>
                {t(`settings.gitlabUrlError.${urlErr}`)}
              </p>
            ) : (
              <p className="text-[10px] text-zinc-500 mt-1">{t('settings.gitlabUrlFormatHint')}</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.gitlabTokenLabel')}</Label>
            <Input
              data-testid={`repo-token-input-${repo.id}`}
              type="password"
              value={form.gitlab_token}
              onChange={(e) => setForm({ ...form, gitlab_token: e.target.value })}
              disabled={!isOwner}
              className="rounded-sm mt-1 text-xs font-mono"
              placeholder={t('settings.gitlabTokenPlaceholder')}
            />
          </div>
        </div>
        {isOwner && (
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={saving || !dirty}
                data-testid={`repo-save-btn-${repo.id}`}
                className="rounded-sm bg-black text-white hover:bg-zinc-800"
              >
                <FloppyDisk size={12} className="mr-1.5" />
                {saving ? t('settings.repoSavingBtn') : t('settings.repoSaveBtn')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={recloning || repo.clone_status === 'cloning' || !!gitlabUrlError(repo.gitlab_url)}
                onClick={reclone}
                data-testid={`repo-reclone-btn-${repo.id}`}
                className="rounded-sm"
                title={gitlabUrlError(repo.gitlab_url) ? t('settings.recloneBlockedByUrl') : undefined}
              >
                <ArrowsClockwise size={12} className="mr-1.5" />
                {recloning ? t('settings.recloneStarting') : t('settings.recloneBtn')}
              </Button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isOnlyRepo}
              onClick={() => setRemoveOpen(true)}
              data-testid={`repo-remove-btn-${repo.id}`}
              className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
              title={isOnlyRepo ? t('settings.repoMinHint') : undefined}
            >
              <Trash size={12} className="mr-1" />
              {t('settings.repoRemoveBtn')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}

function AddRepoInline({ project, onProjectChange, isFirstRepo = false }) {
  const t = useT();
  const [open, setOpen] = useState(isFirstRepo);
  const initialFormState = () => ({
    name: isFirstRepo ? 'main' : '',
    gitlab_url: '',
    gitlab_token: '',
    default_branch: 'main',
    branch_prefix: '',
  });
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const addUrlErr = gitlabUrlError(form.gitlab_url);
  const addUrlInvalid = form.gitlab_url.length > 0 && !!addUrlErr;

  const reset = () => setForm(initialFormState());

  const submit = async (e) => {
    e.preventDefault();
    if (addUrlErr) {
      toast.error(t(`settings.gitlabUrlError.${addUrlErr}`));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name || 'repo',
        gitlab_url: form.gitlab_url.trim(),
        gitlab_token: form.gitlab_token,
        default_branch: form.default_branch || 'main',
        branch_prefix: form.branch_prefix,
      };
      const { data } = await api.post(`/projects/${project.id}/repos`, payload);
      toast.success(t('settings.repoAddedToast'));
      onProjectChange && onProjectChange(data);
      reset();
      if (!isFirstRepo) setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.repoAddFailedToast')));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-sm"
        data-testid="repo-add-open-btn"
        onClick={() => setOpen(true)}
      >
        <Plus size={12} className="mr-1.5" />
        {isFirstRepo ? t('settings.connectFirstRepoBtn') : t('settings.addRepoBtn')}
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50 p-3 space-y-2" data-testid="repo-add-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.repoNameLabel')}</Label>
          <Input
            data-testid="repo-add-name-input"
            value={form.name}
            placeholder={t('settings.repoNamePlaceholder')}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-sm mt-1 text-xs font-mono"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.defaultBranchLabel')}</Label>
          <Input
            data-testid="repo-add-branch-input"
            value={form.default_branch}
            onChange={(e) => setForm({ ...form, default_branch: e.target.value })}
            className="rounded-sm mt-1 text-xs font-mono"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.branchPrefixLabel')}</Label>
          <Input
            data-testid="repo-add-branch-prefix-input"
            value={form.branch_prefix}
            onChange={(e) => setForm({ ...form, branch_prefix: e.target.value })}
            className="rounded-sm mt-1 text-xs font-mono"
            placeholder={t('settings.branchPrefixPlaceholder')}
          />
          <p className="text-[10px] text-zinc-500 mt-1">{t('settings.branchPrefixHint')}</p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.gitlabUrlLabel')}</Label>
          <Input
            data-testid="repo-add-url-input"
            value={form.gitlab_url}
            onChange={(e) => setForm({ ...form, gitlab_url: e.target.value })}
            aria-invalid={addUrlInvalid || undefined}
            className={`rounded-sm mt-1 text-xs font-mono ${addUrlInvalid ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
            placeholder="https://gitlab.com/group/project"
            required
          />
          {addUrlInvalid ? (
            <p className="text-[10px] text-red-600 mt-1" data-testid="repo-add-url-error">
              {t(`settings.gitlabUrlError.${addUrlErr}`)}
            </p>
          ) : (
            <p className="text-[10px] text-zinc-500 mt-1">{t('settings.gitlabUrlFormatHint')}</p>
          )}
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider font-semibold">{t('settings.gitlabTokenLabel')}</Label>
          <Input
            data-testid="repo-add-token-input"
            type="password"
            value={form.gitlab_token}
            onChange={(e) => setForm({ ...form, gitlab_token: e.target.value })}
            className="rounded-sm mt-1 text-xs font-mono"
            placeholder="glpat-..."
            required
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={submitting}
          data-testid="repo-add-submit-btn"
          className="rounded-sm bg-black text-white hover:bg-zinc-800"
        >
          {submitting ? t('settings.addingRepoBtn') : t('settings.addRepoSubmitBtn')}
        </Button>
        {!isFirstRepo && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => { reset(); setOpen(false); }}
            className="rounded-sm"
            data-testid="repo-add-cancel-btn"
          >
            {t('common.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}

export default function ProjectReposPanel({ project, onProjectChange }) {
  const t = useT();
  const { user } = useAuth();
  const isOwner = user && project.owner_id === user.id;
  const repos = project.repos || [];
  const isEmpty = repos.length === 0;

  return (
    <div className="space-y-2" data-testid="project-repos-panel">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
          {t('settings.reposTitle')} ({repos.length})
        </p>
      </div>
      <p className="text-[11px] text-zinc-500">{t('settings.reposDescription')}</p>
      {isEmpty && (
        <div
          data-testid="repos-empty-state"
          className="rounded-sm border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
        >
          {isOwner
            ? t('settings.reposEmptyOwnerHint')
            : t('settings.reposEmptyMemberHint')}
        </div>
      )}
      <div className="space-y-2">
        {repos.map((r) => (
          <RepoRow
            key={r.id}
            project={project}
            repo={r}
            isOnlyRepo={false}
            onProjectChange={onProjectChange}
          />
        ))}
      </div>
      {isOwner && (
        <AddRepoInline
          project={project}
          onProjectChange={onProjectChange}
          isFirstRepo={isEmpty}
        />
      )}
    </div>
  );
}
